import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTenant } from '../contexts/TenantContext';
import { DashboardLayout } from '../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
    Select, 
    SelectContent, 
    SelectItem, 
    SelectTrigger, 
    SelectValue 
} from '../components/ui/select';
import { 
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow 
} from '../components/ui/table';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
    Gift, 
    Clock, 
    CheckCircle2, 
    XCircle, 
    Filter,
    Coins,
    Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatusBadge = ({ status }) => {
    const styles = {
        pending: { bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', label: 'Ausstehend' },
        approved: { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-200', label: 'Genehmigt' },
        rejected: { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', label: 'Abgelehnt' }
    };
    const style = styles[status] || styles.pending;
    
    return (
        <Badge variant="outline" className={`${style.bg} ${style.text} ${style.border}`}>
            {style.label}
        </Badge>
    );
};

export const AdminRedemptionsPage = () => {
    const { token } = useAuth();
    const { tenant } = useTenant();
    
    const [redemptions, setRedemptions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState('all');
    const [selectedRedemption, setSelectedRedemption] = useState(null);
    const [actionType, setActionType] = useState(null); // 'approve' or 'reject'
    const [adminNotes, setAdminNotes] = useState('');
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [redemptionsRes, statsRes] = await Promise.all([
                axios.get(`${API}/admin/redemptions`, {
                    headers: { Authorization: `Bearer ${token}` }
                }),
                axios.get(`${API}/admin/redemptions/stats`, {
                    headers: { Authorization: `Bearer ${token}` }
                })
            ]);
            setRedemptions(redemptionsRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Daten konnten nicht geladen werden');
        } finally {
            setLoading(false);
        }
    };

    const handleProcess = async () => {
        if (!selectedRedemption || !actionType) return;
        
        setProcessing(true);
        try {
            await axios.put(
                `${API}/admin/redemptions/${selectedRedemption.id}`,
                { 
                    status: actionType === 'approve' ? 'approved' : 'rejected',
                    admin_notes: adminNotes || null
                },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(actionType === 'approve' ? 'Einlösung genehmigt' : 'Einlösung abgelehnt');
            setSelectedRedemption(null);
            setActionType(null);
            setAdminNotes('');
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Aktion fehlgeschlagen');
        } finally {
            setProcessing(false);
        }
    };

    const filteredRedemptions = statusFilter === 'all' 
        ? redemptions 
        : redemptions.filter(r => r.status === statusFilter);

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <DashboardLayout>
            <div className="space-y-8 animate-fade-in">
                {/* Header */}
                <div>
                    <h1 className="text-3xl font-bold text-slate-900">Einlösungen</h1>
                    <p className="text-slate-600 mt-1">
                        Verwalten Sie Prämien-Einlösungsanfragen
                    </p>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center">
                                    <Clock size={24} className="text-amber-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Ausstehend</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {stats?.pending || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-green-50 flex items-center justify-center">
                                    <CheckCircle2 size={24} className="text-green-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Genehmigt</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {stats?.approved || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div className="w-12 h-12 rounded-xl bg-red-50 flex items-center justify-center">
                                    <XCircle size={24} className="text-red-600" />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Abgelehnt</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {stats?.rejected || 0}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    <Card>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-4">
                                <div 
                                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                                    style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                >
                                    <Coins size={24} style={{ color: tenant?.primary_color }} />
                                </div>
                                <div>
                                    <p className="text-sm text-slate-500">Eingelöst (Pkt.)</p>
                                    <p className="text-2xl font-bold text-slate-900">
                                        {(stats?.total_points_redeemed || 0).toLocaleString('de-DE')}
                                    </p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Redemptions Table */}
                <Card>
                    <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <CardTitle>Alle Einlösungen</CardTitle>
                        <div className="flex items-center gap-2">
                            <Filter size={18} className="text-slate-400" />
                            <Select value={statusFilter} onValueChange={setStatusFilter}>
                                <SelectTrigger className="w-40">
                                    <SelectValue placeholder="Status filtern" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">Alle Status</SelectItem>
                                    <SelectItem value="pending">Ausstehend</SelectItem>
                                    <SelectItem value="approved">Genehmigt</SelectItem>
                                    <SelectItem value="rejected">Abgelehnt</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="space-y-3">
                                {[1, 2, 3, 4, 5].map(i => (
                                    <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
                                ))}
                            </div>
                        ) : filteredRedemptions.length === 0 ? (
                            <div className="text-center py-12">
                                <Gift size={48} className="mx-auto text-slate-300 mb-3" />
                                <p className="text-slate-500">Keine Einlösungen gefunden</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Prämie</TableHead>
                                            <TableHead>Kunde</TableHead>
                                            <TableHead>Punkte</TableHead>
                                            <TableHead>Datum</TableHead>
                                            <TableHead>Status</TableHead>
                                            <TableHead className="text-right">Aktion</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredRedemptions.map((redemption) => (
                                            <TableRow key={redemption.id}>
                                                <TableCell>
                                                    <div className="flex items-center gap-3">
                                                        <div 
                                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                                            style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                                        >
                                                            <Gift size={18} style={{ color: tenant?.primary_color }} />
                                                        </div>
                                                        <span className="font-medium">{redemption.reward_title}</span>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <div>
                                                        <p className="font-medium text-slate-900">{redemption.user_name}</p>
                                                        <p className="text-sm text-slate-500">{redemption.user_email}</p>
                                                    </div>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="font-semibold">
                                                        {redemption.points_used.toLocaleString('de-DE')}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <span className="text-slate-600">
                                                        {formatDate(redemption.created_at)}
                                                    </span>
                                                </TableCell>
                                                <TableCell>
                                                    <StatusBadge status={redemption.status} />
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {redemption.status === 'pending' ? (
                                                        <div className="flex justify-end gap-2">
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                                                onClick={() => {
                                                                    setSelectedRedemption(redemption);
                                                                    setActionType('approve');
                                                                }}
                                                            >
                                                                <CheckCircle2 size={16} className="mr-1" />
                                                                Genehmigen
                                                            </Button>
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                                                onClick={() => {
                                                                    setSelectedRedemption(redemption);
                                                                    setActionType('reject');
                                                                }}
                                                            >
                                                                <XCircle size={16} className="mr-1" />
                                                                Ablehnen
                                                            </Button>
                                                        </div>
                                                    ) : (
                                                        <span className="text-sm text-slate-400">
                                                            {redemption.processed_at && formatDate(redemption.processed_at)}
                                                        </span>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>

            {/* Confirmation Dialog */}
            <Dialog open={!!selectedRedemption && !!actionType} onOpenChange={() => {
                setSelectedRedemption(null);
                setActionType(null);
                setAdminNotes('');
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {actionType === 'approve' ? 'Einlösung genehmigen' : 'Einlösung ablehnen'}
                        </DialogTitle>
                        <DialogDescription>
                            {actionType === 'approve' 
                                ? 'Die Prämie wird für den Kunden freigegeben.'
                                : 'Die Punkte werden dem Kunden zurückerstattet.'
                            }
                        </DialogDescription>
                    </DialogHeader>
                    
                    {selectedRedemption && (
                        <div className="py-4 space-y-4">
                            <div className="p-4 bg-slate-50 rounded-lg">
                                <div className="flex items-center gap-4">
                                    <div 
                                        className="w-12 h-12 rounded-xl flex items-center justify-center"
                                        style={{ backgroundColor: `${tenant?.primary_color}15` }}
                                    >
                                        <Gift size={24} style={{ color: tenant?.primary_color }} />
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-slate-900">{selectedRedemption.reward_title}</h4>
                                        <p className="text-sm text-slate-500">
                                            {selectedRedemption.user_name} • {selectedRedemption.points_used.toLocaleString('de-DE')} Punkte
                                        </p>
                                    </div>
                                </div>
                            </div>
                            
                            <div className="space-y-2">
                                <Label htmlFor="notes">Notiz (optional)</Label>
                                <Textarea
                                    id="notes"
                                    placeholder="Fügen Sie eine Notiz für den Kunden hinzu..."
                                    value={adminNotes}
                                    onChange={(e) => setAdminNotes(e.target.value)}
                                    rows={3}
                                />
                            </div>
                        </div>
                    )}
                    
                    <DialogFooter>
                        <Button variant="outline" onClick={() => {
                            setSelectedRedemption(null);
                            setActionType(null);
                            setAdminNotes('');
                        }}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={handleProcess}
                            disabled={processing}
                            className={actionType === 'approve' 
                                ? 'bg-green-600 hover:bg-green-700' 
                                : 'bg-red-600 hover:bg-red-700'
                            }
                        >
                            {processing ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    Wird verarbeitet...
                                </>
                            ) : (
                                actionType === 'approve' ? 'Genehmigen' : 'Ablehnen'
                            )}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
};
